import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import {
  type AthleteView,
  type ExternalAppearanceView,
  type ExternalHistoryAttestationView,
  type ExternalHistoryView,
  type PageResponse,
  type SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import {
  appearanceStatusKeys,
  attestationRequest,
  attestationStatusKeys,
  emptyAppearanceForm,
  emptyAttestationForm,
  externalAppearanceRequest,
  externalHistoryListPath,
  externalHistoryPath,
  withdrawExternalHistoryRequest,
  type AttestationForm,
  type ExternalAppearanceForm,
  type ExternalHistoryKind,
} from './externalHistory';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';

export interface ExternalHistorySectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
}

interface WithdrawPanel {
  kind: ExternalHistoryKind;
  recordId: string;
  reason: string;
}

/**
 * Matches an athlete played outside the club's rosters and the registrar's attestations that those
 * matches are all recorded (ADR 0022). The registrar picks an athlete, records each verified match and
 * attests a stretch of days; a record is withdrawn with a reason and a withdrawn one stays as history.
 * Only a granted attestation lets the workload checks stop reporting incomplete history.
 */
export function ExternalHistorySection({
  locale,
  base,
  csrfToken,
}: ExternalHistorySectionProps): React.JSX.Element {
  const [athleteId, setAthleteId] = useState('');
  const [history, setHistory] = useState<ExternalHistoryView | null>(null);
  const [appearanceForm, setAppearanceForm] = useState<ExternalAppearanceForm>(emptyAppearanceForm);
  const [attestationForm, setAttestationForm] = useState<AttestationForm>(emptyAttestationForm);
  const [panel, setPanel] = useState<WithdrawPanel | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);

  const load = useCallback(async () => {
    if (athleteId === '') {
      setHistory(null);
      return;
    }
    try {
      setHistory(await apiRequest<ExternalHistoryView>(externalHistoryListPath(base, athleteId)));
    } catch (error) {
      setHistory(null);
      setErrorKey(errorKeyOf(error));
    }
  }, [base, athleteId]);

  useEffect(() => {
    setPanel(null);
    setErrorKey(null);
    setMessage(null);
    setAppearanceForm(emptyAppearanceForm);
    setAttestationForm(emptyAttestationForm);
    void load();
  }, [load]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function submit(action: () => Promise<unknown>, successKey: TranslationKey): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(successKey);
      setPanel(null);
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
    // After a failure too: a version conflict or a record withdrawn meanwhile shows what the server holds.
    await load();
  }

  const appearance = externalAppearanceRequest(athleteId, appearanceForm);
  const attestation = attestationRequest(athleteId, attestationForm);

  async function recordAppearance(): Promise<void> {
    if (appearance === null) return;
    await submit(async () => {
      await apiRequest<ExternalAppearanceView>(externalHistoryPath(base, { kind: 'appearances' }), {
        method: 'POST',
        body: appearance,
        csrfToken,
      });
      setAppearanceForm(emptyAppearanceForm);
    }, 'externalHistory.appearance.done');
  }

  async function recordAttestation(): Promise<void> {
    if (attestation === null) return;
    await submit(async () => {
      await apiRequest<ExternalHistoryAttestationView>(
        externalHistoryPath(base, { kind: 'attestations' }),
        { method: 'POST', body: attestation, csrfToken },
      );
      setAttestationForm(emptyAttestationForm);
    }, 'externalHistory.attestation.done');
  }

  async function withdraw(open: WithdrawPanel, version: number): Promise<void> {
    const request = withdrawExternalHistoryRequest(open.reason);
    if (request === null) return;
    await submit(async () => {
      await apiRequest<unknown>(
        externalHistoryPath(base, { kind: open.kind, id: open.recordId, withdraw: true }),
        { method: 'POST', body: request, csrfToken, ifMatchVersion: version },
      );
    }, 'externalHistory.withdraw.done');
  }

  /** The reason field and buttons that replace the Withdraw button of one record. */
  function withdrawForm(
    kind: ExternalHistoryKind,
    recordId: string,
    version: number,
  ): React.JSX.Element {
    const open = panel?.kind === kind && panel.recordId === recordId ? panel : null;
    if (open === null) {
      return (
        <Button
          size="small"
          variant="outlined"
          color="error"
          sx={{ mt: 0.5 }}
          onClick={() => {
            setPanel({ kind, recordId, reason: '' });
          }}
        >
          {translate(locale, 'externalHistory.withdraw')}
        </Button>
      );
    }
    return (
      <Stack
        component="form"
        spacing={1.5}
        sx={{ mt: 1, maxWidth: 420 }}
        onSubmit={(event) => {
          event.preventDefault();
          void withdraw(open, version);
        }}
      >
        <TextField
          required
          size="small"
          label={translate(locale, 'externalHistory.withdraw.reason')}
          value={open.reason}
          onChange={(event) => {
            setPanel({ ...open, reason: event.target.value });
          }}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            type="submit"
            variant="contained"
            color="error"
            size="small"
            disabled={withdrawExternalHistoryRequest(open.reason) === null}
          >
            {translate(locale, 'externalHistory.withdraw.submit')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              setPanel(null);
            }}
          >
            {translate(locale, 'externalHistory.cancel')}
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Box component="section" aria-labelledby="external-history-title" sx={{ mt: 3 }}>
      <Typography variant="h6" component="h3" id="external-history-title" gutterBottom>
        {translate(locale, 'externalHistory.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'externalHistory.hint')}
      </Typography>
      <Box sx={{ maxWidth: 480, mb: 2 }}>
        <RemoteSelect
          label={translate(locale, 'externalHistory.field.athlete')}
          noMatchesText={translate(locale, 'externalHistory.noAthletes')}
          load={findAthletes}
          onSelect={setAthleteId}
        />
      </Box>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {athleteId !== '' && history === null && errorKey === null && (
        <Typography>{translate(locale, 'externalHistory.loading')}</Typography>
      )}
      {history !== null && (
        <>
          <Typography variant="subtitle1" component="h4" gutterBottom>
            {translate(locale, 'externalHistory.appearances.title')}
          </Typography>
          {history.appearances.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {translate(locale, 'externalHistory.appearances.empty')}
            </Typography>
          )}
          <Stack
            component="ul"
            spacing={1.5}
            sx={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 3 }}
          >
            {history.appearances.map((record) => (
              <Box component="li" key={record.id}>
                <Typography variant="subtitle2">
                  {formatCalendarDate(record.playedOn, locale)} · {record.competitionName} ·{' '}
                  {translate(locale, appearanceStatusKeys[record.status])}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'externalHistory.appearance.source')}: {record.sourceReference}
                </Typography>
                {record.withdrawalReason !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {translate(locale, 'externalHistory.withdrawnReason')}:{' '}
                    {record.withdrawalReason}
                  </Typography>
                )}
                {history.canManage &&
                  record.status === 'recorded' &&
                  withdrawForm('appearances', record.id, record.version)}
              </Box>
            ))}
          </Stack>
          {history.canManage && (
            <Stack
              component="form"
              spacing={2}
              sx={{ maxWidth: 480, mb: 3 }}
              onSubmit={(event) => {
                event.preventDefault();
                void recordAppearance();
              }}
            >
              <Typography variant="subtitle1" component="h4">
                {translate(locale, 'externalHistory.appearance.create.title')}
              </Typography>
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'externalHistory.appearance.field.playedOn')}
                value={appearanceForm.playedOn}
                onChange={(event) => {
                  setAppearanceForm({ ...appearanceForm, playedOn: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                required
                size="small"
                label={translate(locale, 'externalHistory.appearance.field.competition')}
                value={appearanceForm.competitionName}
                onChange={(event) => {
                  setAppearanceForm({ ...appearanceForm, competitionName: event.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <TextField
                required
                size="small"
                label={translate(locale, 'externalHistory.appearance.field.source')}
                helperText={translate(locale, 'externalHistory.appearance.field.sourceHint')}
                value={appearanceForm.sourceReference}
                onChange={(event) => {
                  setAppearanceForm({ ...appearanceForm, sourceReference: event.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: 200 } }}
              />
              <Box>
                <Button type="submit" variant="contained" disabled={appearance === null}>
                  {translate(locale, 'externalHistory.appearance.submit')}
                </Button>
              </Box>
            </Stack>
          )}
          <Typography variant="subtitle1" component="h4" gutterBottom>
            {translate(locale, 'externalHistory.attestations.title')}
          </Typography>
          {history.attestations.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              {translate(locale, 'externalHistory.attestations.empty')}
            </Typography>
          )}
          <Stack
            component="ul"
            spacing={1.5}
            sx={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 3 }}
          >
            {history.attestations.map((record) => (
              <Box component="li" key={record.id}>
                <Typography variant="subtitle2">
                  {translate(locale, 'externalHistory.attestation.covers')
                    .replace('{from}', formatCalendarDate(record.coversFrom, locale))
                    .replace('{through}', formatCalendarDate(record.coversThrough, locale))}{' '}
                  · {translate(locale, attestationStatusKeys[record.status])}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {record.statement}
                </Typography>
                {record.withdrawalReason !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {translate(locale, 'externalHistory.withdrawnReason')}:{' '}
                    {record.withdrawalReason}
                  </Typography>
                )}
                {history.canManage &&
                  record.status === 'granted' &&
                  withdrawForm('attestations', record.id, record.version)}
              </Box>
            ))}
          </Stack>
          {history.canManage && (
            <Stack
              component="form"
              spacing={2}
              sx={{ maxWidth: 480 }}
              onSubmit={(event) => {
                event.preventDefault();
                void recordAttestation();
              }}
            >
              <Typography variant="subtitle1" component="h4">
                {translate(locale, 'externalHistory.attestation.create.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {translate(locale, 'externalHistory.attestation.hint')}
              </Typography>
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'externalHistory.attestation.field.from')}
                value={attestationForm.coversFrom}
                onChange={(event) => {
                  setAttestationForm({ ...attestationForm, coversFrom: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                required
                size="small"
                type="date"
                label={translate(locale, 'externalHistory.attestation.field.through')}
                value={attestationForm.coversThrough}
                onChange={(event) => {
                  setAttestationForm({ ...attestationForm, coversThrough: event.target.value });
                }}
                slotProps={{ inputLabel: { shrink: true } }}
              />
              <TextField
                required
                size="small"
                label={translate(locale, 'externalHistory.attestation.field.statement')}
                value={attestationForm.statement}
                onChange={(event) => {
                  setAttestationForm({ ...attestationForm, statement: event.target.value });
                }}
                slotProps={{ htmlInput: { maxLength: 500 } }}
              />
              <Box>
                <Button type="submit" variant="contained" disabled={attestation === null}>
                  {translate(locale, 'externalHistory.attestation.submit')}
                </Button>
              </Box>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
